import { useTranslation } from 'react-i18next'
import { Check } from 'lucide-react'
import { AuthModal, useAuthModal } from '@/features/auth'
import { Logo, LocalizedLink, SEO } from '@/shared/components'

/**
 * Pricing Page - Standalone
 * Shows the 4 pricing plans
 */
export function PricingPage() {
  const { t } = useTranslation()
  const { isOpen, openAuthModal, closeAuthModal } = useAuthModal()

  const plans = [
    {
      id: 'one-shot',
      name: 'Single Scoping',
      price: 29,
      priceOriginal: 49,
      badge: 'Launch price',
      type: 'one_time',
      features: ['1 project', 'Unlimited versions', 'PDF export', 'NicolAI assistance'],
    },
    {
      id: 'starter',
      name: 'Starter',
      price: 299,
      type: 'recurring',
      features: ['5 projects / month', 'Unlimited versions', 'PDF export', 'NicolAI assistance', 'Email support'],
    },
    {
      id: 'pro',
      name: 'Pro',
      price: 499,
      priceOriginal: 599,
      type: 'recurring',
      badge: 'Most popular',
      features: ['10 projects / month', 'Unlimited versions', 'PDF export', 'NicolAI assistance', 'Priority support', 'Team collaboration'],
    },
    {
      id: 'agency',
      name: 'Agency',
      price: 899,
      type: 'recurring',
      features: ['20 projects / month', 'Unlimited versions', 'PDF export', 'NicolAI assistance', 'Priority support', 'Team collaboration', 'Custom branding (soon)'],
    },
  ]

  return (
    <div className="min-h-screen bg-white">
      <SEO
        titleKey="seo.pricing.title"
        descriptionKey="seo.pricing.description"
      />
      {/* Header */}
      <header className="border-b border-border">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6">
          <LocalizedLink to="/" className="flex items-center gap-2">
            <Logo className="h-8 w-8 text-primary" />
            <span className="text-lg font-semibold text-foreground">HeyNicolai</span>
          </LocalizedLink>
          <div className="flex items-center gap-4">
            <button
              onClick={() => openAuthModal()}
              className="text-sm font-medium text-foreground-muted hover:text-foreground"
            >
              {t('auth.signIn')}
            </button>
            <button
              onClick={() => openAuthModal()}
              className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover"
            >
              {t('auth.startScoping')}
            </button>
          </div>
        </div>
      </header>

      {/* Pricing Section */}
      <main className="mx-auto max-w-7xl px-6 py-24">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-foreground">Simple, transparent pricing</h1>
          <p className="mt-4 text-lg text-foreground-muted">
            Start with a single scoping or subscribe for unlimited power.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`relative rounded-xl border p-6 ${
                plan.badge
                  ? 'border-accent ring-2 ring-accent'
                  : 'border-border'
              }`}
            >
              {plan.badge && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-accent px-3 py-1 text-xs font-medium text-white">
                  {plan.badge}
                </span>
              )}

              <div className="text-center">
                <h3 className="text-lg font-semibold text-foreground">{plan.name}</h3>
                <div className="mt-4">
                  {plan.priceOriginal && (
                    <span className="mr-2 text-foreground-muted line-through">
                      {plan.priceOriginal}€
                    </span>
                  )}
                  <span className="text-4xl font-bold text-foreground">{plan.price}€</span>
                  {plan.type === 'recurring' && (
                    <span className="text-foreground-muted">/mo</span>
                  )}
                </div>
              </div>

              <ul className="mt-6 space-y-3">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-success" />
                    <span className="text-foreground-muted">{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => openAuthModal()}
                className={`mt-6 w-full rounded-lg px-4 py-2.5 text-sm font-medium ${
                  plan.badge
                    ? 'bg-accent text-white hover:bg-accent-hover'
                    : 'border border-border text-foreground hover:bg-background-secondary'
                }`}
              >
                Get started
              </button>
            </div>
          ))}
        </div>
      </main>

      {/* Auth Modal */}
      <AuthModal isOpen={isOpen} onClose={closeAuthModal} />
    </div>
  )
}
